import * as behaviors from 'rendering/behaviors';
import * as is from 'utilities/is';
import { ACTIONS, PASSIVE_EVENTS } from 'constants/annotations';
import { unique } from './list'

// "wrapped" classes should not be generated more than once
// to prevent unncessary Preact component re-rendering 
export const appliedBehaviors = {};

export const IN_PLAYER_INLINE_WINDOW_STYLES = {
    left: '5%', top: '5%', width: '90%', height: '90%'
};

export const IN_ADJACENT_INLINE_WINDOW_STYLES = {
    left: 0, top: 0, width: '100%', height: '100%'
};

export const INLINE_WINDOW_ID_BASE = 4000;

export const generateAnnotationConfig = (type, config = {}) => {
    let generated = {};

    switch(type) {
        case 'mute':
            generated = {
                id: 8000,
                type: 'mute',
                target: 'player',
                left: '0',
                top: '0',
                width: 'auto',
                height: 'auto',
                start: 0,
                properties: {}
            };
            break;

        case 'inlinewindow':
            const overrideStyles = config.styles || {};
            delete config.styles; // config get's merged with `generated`, so `config.styles` would wipe out generated `styles`

            generated = {
                id: `${INLINE_WINDOW_ID_BASE}-${config.target}`,
                type: 'iframe',
                styles: {
                    position: 'absolute',
                    zIndex: 1,
                    ...(config.target === 'player' ? IN_PLAYER_INLINE_WINDOW_STYLES : IN_ADJACENT_INLINE_WINDOW_STYLES),
                    ...overrideStyles
                },
                properties: {},
                behaviors: ['closeable'],
                actions: (
                    config.target === 'player' ?
                        [
                            {
                                "event": "onActivate",
                                "action": "pause",
                                "args": {}
                            },
                            {
                                "event": "onDeactivate",
                                "action": "play",
                                "args": {}
                            }
                        ] : []
                )
            }
    }

    return { ...generated, ...config };
};

export const generateInlineLinkAnnotation = ({ id, target, url, styles = {}, actions = [] }) => {
    const displayHandlers = (
        actions
            .filter(a => a.event === 'onLinkOpen' || a.event === 'onLinkClose')
            .map(a => ({
                 ...a,
                event: a.event === 'onLinkOpen' ? 'onActivate' : 'onDeactivate',
                args: {
                    id,
                    ...a.args
                }
            }))
    );

    return generateAnnotationConfig('inlinewindow', {
        target,
        styles,
        content: url,
        actions: displayHandlers
    })
}

export const captionToBookmark = caption => {
    if (caption.contentType !== 'string') {
        // if content type is URL, don't try to convert to bookmark
        return [];
    }

    caption = caption.content;

    // strip WEBVTT literal
    caption = caption.replace('WEBVTT\n\n', '');
    
    // Segment by time
    caption = caption.split(/[0-9]\n([0-9:.]+ --> [0-9:.]+)\n(.*)/);
    
    // Serialize to JSON
    caption = caption.reduce((collected, c, i) => {
        
        // Store time in one property
        if (c.match(/([0-9:.]+ --> [0-9:.]+)/)) {
            collected.push({ start: c, content: '' });
        
        // captions in another
        } else if (collected[collected.length - 1]) {
            collected[collected.length - 1].content += c;
        }

        return collected;
    }, []);

    // Parse start and end times
    caption = caption.map(c => {
        const [start, end] = c.start.split(' --> ');
        let [minute, second] = start.split(':');

        c.start = Number(minute) * 60 + Number(second);

        [minute, second] = end.split(':');
        c.end = Number(minute) * 60 + Number(second);

        return c;
    });

    return caption;
};

export const doesPassivelyPause = (actions = []) => (
    actions
        .filter(a => PASSIVE_EVENTS.includes(a.event))
        .some(a => a.action === ACTIONS.PAUSE)
);

export const applyBehaviors = (config, AnnotationComponent) => {
    if (config.behaviors && config.behaviors.length) {
        const compositionKey = `${config.type}|${config.target}|${config.behaviors.join('|')}`;

        if (!appliedBehaviors[compositionKey]) {
            appliedBehaviors[compositionKey] = config.behaviors.reduce((augmented, behavior) => (
                behaviors[behavior] ? behaviors[behavior](augmented) : augmented
            ), AnnotationComponent);
        }

        return appliedBehaviors[compositionKey];
    }

    return AnnotationComponent;
};

export const generateLanguageVariation = (language = 'en', changes = {}) => {
    const changeSummary = Object.keys(changes).map(k => `${k}-${changes[k]}`).join('-');

    return {
        variations: [
            {
                // "unique" ids are used to prevent duplicate variations from being added
                id: `language-equal-${language}-${changeSummary}`,
                conditions: [
                    {
                        state: 'language',
                        value: language,
                        assertion: 'equal'
                    }
                ],
                changes
            }
        ]
    }
};

export const generateVariation = (conditions = {}, changes = {}) => {
    const conditionSummary = Object.keys(conditions).map(k => `${k}-${conditions[k]}`).join('-');
    const changeSummary = Object.keys(changes).map(k => `${k}-${changes[k]}`).join('-');

    return {
        variations: [
            {
                // "unique" ids are used to prevent duplicate variations from being added
                id: `${conditionSummary}-${changeSummary}`,
                conditions: Object.keys(conditions).map(key => ({
                    state: key,
                    value: conditions[key],
                    assertion: 'equal'
                })),
                changes
            }
        ]
    }
}

export const mergeAnnotations = (a,b) => {
    const {
        styles = {},
        actions = [],
        properties = {},
        variations = [],
        behaviors = [], 
        ...mods 
    } = b;

    const merged = {
        ...a,
        ...mods,
        
        // these operations could be done more generically, but we want to leverage
        // knowledge of the model to prevent iterating and inspecting every property
        // also, using native object merge methods will improve performance 
        styles: (            
            Object.keys(styles).reduce((styleMods, key) => {
                if (is.compositionType(key) && a.styles[key]) {
                    styleMods[key] = {
                        ...a.styles[key],
                        ...styles[key]
                    }
                } else {
                    styleMods[key] = styles[key];
                }

                return styleMods;
            }, (a.styles || {}))
        ),
        actions: [
            ...(a.actions || []),
            ...actions
        ],
        properties: (
            Object.keys(properties).reduce((propsMods, key) => {
                if (typeof properties[key] === 'object' && properties[key] !== null && propsMods[key]) {
                    propsMods[key] = { ...propsMods[key], ...properties[key] }
                } else {
                    propsMods[key] = properties[key];
                }

                return propsMods;
            }, (a.properties || {}))
        ),
        behaviors: [
            ...(a.behaviors || []),
            ...behaviors
        ]
    };

    merged.variations = merged.variations || [];

    // prevent variation duplicates
    if (variations.length) {
        const ids = variations.map(v => v.id);

        merged.variations = [
            ...merged.variations.filter(v => (
                !v.id || !ids.includes(v.id)
            )),
            ...variations
        ]
    }

    return merged;
};

export const modifyClasses = (classToAdd = '', currentClasses = '', action) => {
    return (
        classToAdd
            .split(' ')
            .reduce((existing, newClass) => {
                if (action === 'addClass') {
                    existing.push(newClass)
                } else if (action === 'removeClass') {
                    existing = existing.filter(e => e !== newClass)
                }
                return existing;
            }, currentClasses.split(' '))
            .filter(c => c !== '')
            .filter(unique)
            .join(' ')
    )
}

export const templatizableProperties = [
    'id', 
    'type',
    'start',
    'end',
    'content',
    'contentType',
    'target',
    'styles',
    'properties',
    'actions',
    'behaviors', 
    'variations'
]